import React, { useState } from "react";
import { useFormikContext } from "formik";
import baseStyles from "../../style.module.scss";
import styles from "./style.module.scss";
import ReviewHeader from "../common/ReviewHeader";
import equipmentPlaceholderImg from "../../../../../../../../../../images/v2/equipment_placeholder.png";
import SkeletonLoader from "../../../../../../../../components/skeleton-loader";
import { getServiceClubFee } from "../../../../../../../../utils/serviceClubPricing";

const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
};

const SectionHeader = ({ title }) => (
  <div className={baseStyles.sectionHeader}>
    <span className={baseStyles.sectionTitle}>{title}</span>
    <div className={baseStyles.sectionLine} />
  </div>
);

const EquipmentItem = ({ item }) => {
  const equipmentData = item?.terminal ? item.terminal : item?.gateway;
  const [imageLoading, setImageLoading] = useState(true);
  return (
    <div className={baseStyles.detailItem}>
      <div className={styles.detailValue}>
        <div className={styles.equipmentInfo}>
          <div
            className={`${styles.equipmentThumbnail} ${
              imageLoading ? styles.loading : ""
            }`}
          >
            <img
              onLoad={() => {
                setImageLoading(false);
              }}
              src={
                equipmentData?.equipment_image
                  ? `/get-image-by-path?path=${equipmentData.equipment_image}`
                  : equipmentPlaceholderImg
              }
              alt={equipmentData?.equipment_name || item.equipment_name}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = equipmentPlaceholderImg;
              }}
              style={{ display: imageLoading ? "none" : "block" }}
            />
          </div>

          {imageLoading ? (
            <SkeletonLoader
              width={48}
              height={48}
              variant="rectangular"
              style={{
                borderRadius: "8px",
              }}
            />
          ) : null}
          <div className={styles.equipmentContent}>
            <div className={styles.equipmentName}>
              <span className={styles.nameText}>
                {item.equipment_name || equipmentData?.equipment_name}
              </span>
              {item.type && (
                <span
                  className={`${styles.tag} ${styles[item.type.toLowerCase()]}`}
                >
                  {item.type === "purchase_for"
                    ? "buy"
                    : item.type === "rent_for"
                      ? "rent"
                      : item.type === "lease_for"
                        ? "lease"
                        : item.type === "monthly_fee"
                          ? "monthly"
                          : item.type === "apply_tpp"
                            ? "tpp"
                            : item.type === "saas"
                              ? "SaaS"
                              : item.type}
                </span>
              )}
            </div>
            <div className={styles.equipmentQuantity}>X {item.quantity}</div>
          </div>
        </div>
        <div className={styles.equipmentPrice}>
          {formatCurrency(item.price)}
        </div>
      </div>
    </div>
  );
};

const TotalRow = ({ label, amount }) => (
  <div className={`${baseStyles.detailItem} ${styles.totalItem}`}>
    <div className={styles.detailValue}>
      <span className={styles.totalLabel}>{label}</span>
      <span className={styles.totalValue}>{formatCurrency(amount)}</span>
    </div>
  </div>
);

const Equipment = ({ onEdit, isOpen, onToggle }) => {
  const { values } = useFormikContext();

  const purchases =
    values?.ems_boot_equipments
      ?.filter((equipment) => equipment.purchase_for != null)
      .map((equipment) => ({
        ...equipment,
        price: parseFloat(equipment.purchase_for) * equipment.quantity,
        type: "purchase_for",
      })) || [];

  const monthlyCosts =
    values?.ems_boot_equipments
      ?.filter(
        (equipment) =>
          equipment.rent_for != null ||
          equipment.lease_for != null ||
          equipment.monthly_fee != null ||
          getServiceClubFee(equipment) !== null,
      )
      .map((equipment) => {
        let price = 0;
        let type = "";

        if (equipment.rent_for && equipment.rent_for > 0) {
          price = parseFloat(equipment.rent_for);
          type = "rent_for";
        } else if (equipment.lease_for && equipment.lease_for > 0) {
          price = parseFloat(equipment.lease_for);
          type = "lease_for";
        } else if (
          (equipment.monthly_fee && equipment.monthly_fee > 0) ||
          getServiceClubFee(equipment) !== null
        ) {
          price = getServiceClubFee(equipment) ?? parseFloat(equipment.monthly_fee);
          type = "monthly_fee";
        }

        return {
          ...equipment,
          price: price * equipment.quantity,
          type,
        };
      }) || [];

  const tppHardware =
    values?.ems_boot_equipments
      ?.filter((equipment) => equipment.tpp && equipment.tpp === "yes")
      .map((equipment) => ({
        ...equipment,
        price: 0,
        type: "apply_tpp",
      })) || [];

  const saasFees =
    values?.ems_boot_equipments
      ?.filter((equipment) => equipment.saas_fee != null)
      .map((equipment) => {
        const equipmentData = equipment?.terminal
          ? equipment.terminal
          : equipment?.gateway;
        return {
          ...equipment,
          price: parseFloat(equipment.saas_fee) * equipment.quantity,
          type: "saas",
          equipment_name: `SaaS fee for ${equipmentData?.equipment_name || equipment.equipment_name}`,
        };
      }) || [];

  const allMonthlyCosts = [...monthlyCosts, ...saasFees];

  const purchaseTotal = purchases.reduce(
    (total, item) => total + item.price,
    0,
  );

  const monthlyTotal = allMonthlyCosts.reduce(
    (total, item) => total + item.price,
    0,
  );

  return (
    <div className={baseStyles.reviewSectionContainer}>
      <ReviewHeader
        title="Equipment"
        isOpen={isOpen}
        onToggle={onToggle}
        onEdit={onEdit}
      />

      {isOpen && (
        <div className={baseStyles.reviewContent}>
          {purchases.length > 0 && (
            <div className={baseStyles.section}>
              <SectionHeader title="PURCHASES" />
              <div className={styles.detailsGrid}>
                {purchases.map((item, index) => (
                  <EquipmentItem
                    key={`purchase-${item.id}-${index}`}
                    item={item}
                  />
                ))}
                <TotalRow label="Total" amount={purchaseTotal} />
              </div>
            </div>
          )}

          {allMonthlyCosts.length > 0 && (
            <div className={baseStyles.section}>
              <SectionHeader title="MONTHLY COSTS" />
              <div className={styles.detailsGrid}>
                {allMonthlyCosts.map((item, index) => (
                  <EquipmentItem
                    key={`monthly-${item.id}-${index}`}
                    item={item}
                  />
                ))}
                <TotalRow label="Total" amount={monthlyTotal} />
              </div>
            </div>
          )}

          {tppHardware.length > 0 && (
            <div className={baseStyles.section}>
              <SectionHeader title="TPP HARDWARE" />
              <div className={styles.detailsGrid}>
                {tppHardware.map((item, index) => (
                  <EquipmentItem key={`tpp-${item.id}-${index}`} item={item} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Equipment;
